import { useState } from "react";
import { useLegacyEffect } from "helper/customHook";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation, useMatch } from "react-router-dom";

import { Collapse, List } from "@mui/material";

import { ReactComponent as ArrowDown } from "assets/svg/chevron_down.svg";
import { ReactComponent as ArrowRight } from "assets/svg/chevron_right.svg";

import NavSubItem from "../NavSubItem";

import {
  NavGroupWrapper,
  NavIconWrapper,
  NavMenuWrapper,
  NavTextWrapper,
} from "../styles";

import { NavGroupData, NavSubItemData, VALUE_SIDE_NAV_PARENTS } from "../const";
import { logoutStart } from "modules/Auth/redux/logout";
import { motion } from "framer-motion";
import { updateDeviceStatusStart } from "modules/Notification/redux/updateDeviceStatus";
import { resetAccountInfo } from "../redux/accountInfo";
import { accountInfoDataSelector } from "../redux/selector";
import { isArray } from "lodash";
import { getPermissionItemStart } from "../redux/roleItem";

interface NavGroupProps {
  navGroupData: NavGroupData;
  isMiniSidenav: boolean;
  position?: "top" | "bottom";
}

function NavGroup({
  navGroupData,
  isMiniSidenav,
  position = "top",
}: NavGroupProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { notiActive } = useSelector(accountInfoDataSelector);

  const { label, icon, navSubList, path, parameter, screenKey } = navGroupData;

  const match = useMatch(parameter || "");

  const isHaveNavSub = navSubList && navSubList.length > 0;
  const isActive =
    (parameter && !!match) ||
    (isHaveNavSub
      ? navSubList.some((item) =>
          pathname.startsWith(item.parameter.split(":")[0])
        )
      : pathname.startsWith(path));

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleToggle = () => {
    if (navGroupData.value === VALUE_SIDE_NAV_PARENTS.LOG_OUT) {
      const deviceId = localStorage.getItem("deviceId");
      if (deviceId && notiActive) {
        dispatch(
          updateDeviceStatusStart({
            name: deviceId,
            status: "INACTIVE",
            callback: () => {
              dispatch(logoutStart({ toastMsg: "Đăng xuất thành công" }));
              dispatch(resetAccountInfo());
            },
          })
        );
      } else {
        dispatch(logoutStart({ toastMsg: "Đăng xuất thành công" }));
        dispatch(resetAccountInfo());
      }

      return;
    }

    if (isHaveNavSub) {
      setIsOpen(!isOpen); // có nav con thì mở ra chọn tiếp
      return;
    } else {
      if (path) navigate(path);
      // ko có nav con thì route luôn đến màn hình
    }
  };

  useLegacyEffect(() => {
    setIsOpen(isActive);
  }, []);

  useLegacyEffect(() => {
    if (isActive && path) {
      if (!screenKey || isArray(screenKey)) return;
      dispatch(getPermissionItemStart(screenKey));
    }
  }, [isActive, path, screenKey]);

  return (
    <NavMenuWrapper isActive={isActive}>
      <NavGroupWrapper
        isActive={isActive}
        onClick={handleToggle}
        position={position}
      >
        <NavIconWrapper>{icon ?? null}</NavIconWrapper>

        <NavTextWrapper>
          {!isMiniSidenav && (
            <motion.div
              key="nav-text"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -10 }}
              transition={{
                exit: { duration: 0.3, delay: 0.3 },
                animate: { duration: 0.1, delay: 0.3 },
              }}
              // style={{ whiteSpace: "nowrap" }}
            >
              {label}
            </motion.div>
          )}
        </NavTextWrapper>

        {isHaveNavSub && (isOpen ? <ArrowDown /> : <ArrowRight />)}
      </NavGroupWrapper>

      <Collapse in={isOpen && isHaveNavSub} timeout="auto">
        <List component="div" disablePadding>
          {navSubList?.map((item: NavSubItemData) => (
            <NavSubItem
              key={item.subValue}
              isMiniSidenav={isMiniSidenav}
              navSubItem={item}
              navGroupData={navGroupData}
              isActiveGroup={isActive}
              isActive={pathname.startsWith(item.parameter.split(":")[0])}
            />
          ))}
        </List>
      </Collapse>
    </NavMenuWrapper>
  );
}

export default NavGroup;
